import React, { createContext, useContext, useRef, useState, useCallback } from 'react';
import { getDashboardChangesAnalysis } from '../fb-functions';
import { useUserId } from './AuthContext';
import { UserLocationData } from './LocationDataContext';

// Create the MarketReportContext
const MarketReportContext = createContext();

// Custom hook to access context
export const useMarketReportData = () => useContext(MarketReportContext);

function stripToken(obj){
  const newObj = {...obj};
  delete newObj['token'];
  // console.log('stripped token obj', newObj);
  return newObj;
}

function hashCode(obj) {
  const jsonString = JSON.stringify(obj);
  let hash = 0;
  for (let i = 0; i < jsonString.length; i++) {
      const char = jsonString.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash |= 0; // Convert to 32-bit integer
  }
  return hash.toString(16); // Convert to hex for a shorter ID
}

export const MarketReportDataProvider = ({ children }) => {
  const { locationData } = UserLocationData();
  const { uid, email } = useUserId();

  const dataCacheRef = useRef({});
  const pendingFetchesRef = useRef({});

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(
    async (specs) => {
      if (!locationData) {
        setError("Location data not available");
        return null;
      }

      const specsTokenStripped = stripToken(specs);
      const specsHash = hashCode(specsTokenStripped);

      if (dataCacheRef.current[specsHash]) {
        console.warn(`Cache hit for ${specsHash}`);
        return dataCacheRef.current[specsHash];
      }

      if (pendingFetchesRef.current[specsHash]) {
        console.warn(`Pending request for ${specsHash}`);
        return pendingFetchesRef.current[specsHash];
      }

      setLoading(true);
      setError(null);

      const fetchPromise = getDashboardChangesAnalysis(specs, uid, email, locationData)
        .then((result) => {
          if (result.success) {
            dataCacheRef.current[specsHash] = result.data;
            return result.data;
          } else {
            setError(`Failed to fetch data for ${specsHash}`);
            return null;
          }
        })
        .catch((err) => {
          setError(`Error fetching data for ${specsHash}: ${err.message}`);
          return null;
        })
        .finally(() => {
          delete pendingFetchesRef.current[specsHash];
          setLoading(false);
        });

      pendingFetchesRef.current[specsHash] = fetchPromise;
      return fetchPromise;
    },
    [uid, email, locationData]
  );

  const value = React.useMemo(
    () => ({
      dataCache: dataCacheRef.current,
      loading,
      error,
      fetchData,
    }),
    [loading, error, fetchData]
  );

  return (
    <MarketReportContext.Provider value={value}>
      {children}
    </MarketReportContext.Provider>
  );
};
